// One order for a person's items, made from the stored answers about each
// item and the ranking of their outcomes (CER-180). A classifier model
// answered fixed questions about every item (supabase/functions/judge-items);
// this file is the ordinary code that combines those answers. It never asks
// a model anything, and it reads no dates: due dates and focus picks are
// handled where Today is built, and win over any rank.
//
// The rank only orders items against each other. It is never a pass mark,
// and an item the model was unsure about is left unranked and said to be so,
// not guessed. It imports nothing but types, so Deno can load it as it is.

import type { GtdToday, GtdTodayTask } from './gtd.ts';
import type { Outcome } from './outcomes.ts';

/** One stored answer, as public.item_judgments holds it. */
export interface ItemJudgment {
  item_type: 'task' | 'note';
  item_id: string;
  question: string;
  /** The model's answer in its own shape: a noul, a choice or a score. */
  answer: unknown;
  /** For the `serves` question: the outcome it named, or null for none. */
  outcome_id: string | null;
  judged_at: string;
}

/** my_item_judgments()'s answer: every stored answer for one kind of item; throws when it is not that. */
export function parseItemJudgments(data: unknown): ItemJudgment[] {
  const malformed = () => new Error('the scores for your items did not arrive');
  if (!Array.isArray(data)) throw malformed();
  return data.map((row): ItemJudgment => {
    if (!isRecord(row) || (row.item_type !== 'task' && row.item_type !== 'note') || typeof row.item_id !== 'string'
      || typeof row.question !== 'string' || !(row.outcome_id === null || typeof row.outcome_id === 'string') || typeof row.judged_at !== 'string') {
      throw malformed();
    }
    return { item_type: row.item_type, item_id: row.item_id, question: row.question, answer: row.answer, outcome_id: row.outcome_id, judged_at: row.judged_at };
  });
}

/** Below this confidence in which outcome an item serves, the item is left unranked. */
export const SERVES_MIN_CONFIDENCE = 0.5;

export type ItemRank =
  /** `score` orders items, larger first. `outcomeId` is the outcome it serves, or null when it serves none (score 0). */
  | { ranked: true; score: number; outcomeId: string | null; size: number | null }
  /** Why there is no rank: nothing stored yet, the model was unsure, or the outcome it named is no longer on the list. */
  | { ranked: false; reason: 'not_judged' | 'unsure' | 'outcome_gone' };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const probability = (value: unknown): number | null => (typeof value === 'number' && value >= 0 && value <= 1 ? value : null);

/** A noul answer's probability of yes; null when the answer is not one. */
function noul(answer: unknown): number | null {
  return isRecord(answer) && answer.type === 'noul' ? probability(answer.noul) : null;
}

/** A score answer's level; null when the answer is not one. */
function level(answer: unknown): number | null {
  return isRecord(answer) && answer.type === 'score' && typeof answer.score === 'number' && answer.score >= 0 ? answer.score : null;
}

/** How sure a choice was: its stated confidence, else the probability of what it chose; null when it gave neither. */
function choiceConfidence(answer: unknown): number | null {
  if (!isRecord(answer) || answer.type !== 'choice' || typeof answer.choice !== 'string') return null;
  const stated = probability(answer.confidence);
  if (stated !== null) return stated;
  return isRecord(answer.probabilities) ? probability(answer.probabilities[answer.choice]) : null;
}

/** The levels of the `moves` question (judge-items/questions.ts): 0 not at all .. 3 decisively. */
const MOVES_TOP = 3;

/**
 * The rank of one item from its stored answers.
 *
 *   weight   the served outcome's place: the first of N counts N/N, the last 1/N, none 0;
 *   moves    how much it moves that outcome, (level + 1) / 4;
 *   urgency  1 to 2, by how likely something real is lost if it slips this week;
 *   ready    0.5 to 1, by how likely it can be started as it stands.
 *
 * score = weight x moves x urgency x ready. An item that serves no outcome
 * ranks 0: below everything that serves one, above nothing. The weights are
 * a starting point, to be re-scored against the person's own ordering before
 * any change is adopted.
 */
export function itemRank(judgments: readonly ItemJudgment[], ranked: readonly Outcome[]): ItemRank {
  const answerTo = (question: string) => judgments.find((judgment) => judgment.question === question);
  const serves = answerTo('serves');
  const moves = level(answerTo('moves')?.answer);
  if (!serves || moves === null) return { ranked: false, reason: 'not_judged' };
  const confidence = choiceConfidence(serves.answer);
  if (confidence === null || confidence < SERVES_MIN_CONFIDENCE) return { ranked: false, reason: 'unsure' };
  const size = level(answerTo('size')?.answer);
  if (serves.outcome_id === null) return { ranked: true, score: 0, outcomeId: null, size };

  const place = ranked.findIndex((outcome) => outcome.id === serves.outcome_id);
  if (place < 0) return { ranked: false, reason: 'outcome_gone' };
  const weight = (ranked.length - place) / ranked.length;
  const urgency = 1 + (noul(answerTo('slips')?.answer) ?? 0);
  const ready = 0.5 + 0.5 * (noul(answerTo('actionable')?.answer) ?? 0.5);
  const score = weight * ((Math.min(moves, MOVES_TOP) + 1) / (MOVES_TOP + 1)) * urgency * ready;
  return { ranked: true, score, outcomeId: serves.outcome_id, size };
}

/** Every item's rank, by "type:id", from all of a person's stored answers. */
export function itemRanks(judgments: readonly ItemJudgment[], ranked: readonly Outcome[]): Map<string, ItemRank> {
  const byItem = new Map<string, ItemJudgment[]>();
  for (const judgment of judgments) {
    const key = `${judgment.item_type}:${judgment.item_id}`;
    byItem.set(key, [...(byItem.get(key) ?? []), judgment]);
  }
  return new Map([...byItem].map(([key, answers]) => [key, itemRank(answers, ranked)]));
}

/**
 * Order for two ranks, best first: ranked before unranked, a larger score
 * first, and between equal scores the smaller item first (it is done sooner).
 * Zero when the ranks do not decide, so the caller's own order stands.
 */
export function compareRanks(a: ItemRank | undefined, b: ItemRank | undefined): number {
  const left = a?.ranked ? a : null;
  const right = b?.ranked ? b : null;
  if (!left || !right) return left ? -1 : right ? 1 : 0;
  if (left.score !== right.score) return right.score - left.score;
  return (left.size ?? Number.POSITIVE_INFINITY) - (right.size ?? Number.POSITIVE_INFINITY) || 0;
}

/** What the person sees of the judging (judgment_overview()). Counts arrive as numbers; cost as text or number. */
export interface JudgmentOverview {
  waiting: number;
  failed: number;
  last_error: string | null;
  last_error_at: string | null;
  pass: { started_at: string; finished_at: string | null; items: number; judged: number; input_tokens: number; cost_usd: number } | null;
}

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isTextOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';

/** judgment_overview()'s answer; throws when it is not one, so a broken status is shown as an error, not as "all scored". */
export function parseJudgmentOverview(data: unknown): JudgmentOverview {
  const malformed = () => new Error('the scoring status did not arrive');
  if (!isRecord(data) || !isCount(data.waiting) || !isCount(data.failed) || !isTextOrNull(data.last_error) || !isTextOrNull(data.last_error_at)) {
    throw malformed();
  }
  let pass: JudgmentOverview['pass'] = null;
  if (data.pass !== null) {
    const p = data.pass;
    const cost = isRecord(p) ? Number(p.cost_usd) : Number.NaN;
    const tokens = isRecord(p) ? Number(p.input_tokens) : Number.NaN;
    if (!isRecord(p) || typeof p.started_at !== 'string' || !isTextOrNull(p.finished_at) || !isCount(p.items) || !isCount(p.judged)
      || !Number.isFinite(cost) || cost < 0 || !Number.isFinite(tokens) || tokens < 0) {
      throw malformed();
    }
    pass = { started_at: p.started_at, finished_at: p.finished_at, items: p.items, judged: p.judged, input_tokens: tokens, cost_usd: cost };
  }
  return { waiting: data.waiting, failed: data.failed, last_error: data.last_error, last_error_at: data.last_error_at, pass };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The lines the outcomes screens show about scoring: what is still waiting,
 * what the last full re-scoring came to (how many items, how long, what it
 * cost), and anything that could not be scored, with the last reason. Empty
 * when there is nothing to say.
 */
export function judgmentStatusLines(overview: JudgmentOverview): { text: string; error: boolean }[] {
  const lines: { text: string; error: boolean }[] = [];
  if (overview.waiting > 0) lines.push({ text: `Scoring ${plural(overview.waiting, 'item', 'items')} against your outcomes…`, error: false });
  const pass = overview.pass;
  if (pass?.finished_at) {
    const seconds = Math.max(0, Math.round((Date.parse(pass.finished_at) - Date.parse(pass.started_at)) / 1000));
    const took = seconds < 90 ? plural(seconds, 'second', 'seconds') : plural(Math.round(seconds / 60), 'minute', 'minutes');
    const cost = pass.cost_usd < 0.01 ? 'less than a cent' : `$${pass.cost_usd.toFixed(2)}`;
    lines.push({ text: `Last re-scoring: ${pass.judged} of ${plural(pass.items, 'item', 'items')} in ${took}, costing ${cost}.`, error: false });
  }
  if (overview.failed > 0) {
    lines.push({
      text: `${plural(overview.failed, 'item', 'items')} could not be scored and ${overview.failed === 1 ? 'is' : 'are'} left unranked${overview.last_error ? `: ${overview.last_error}` : ''}.`,
      error: true,
    });
  } else if (overview.waiting > 0 && overview.last_error) {
    lines.push({ text: `The last attempt failed and will be tried again: ${overview.last_error}.`, error: true });
  }
  return lines;
}

/** How many things Today leads with. */
export const TODAY_LEAD_COUNT = 7;

export interface TodayLead<T extends GtdTodayTask> {
  /** The few highest-ranked next actions, best first, each with the outcome it serves. */
  lead: { task: T; outcome: Outcome }[];
  /** Today's view with every context group ordered by the same rank. */
  view: GtdToday<T>;
  /** Next actions with no confident rank, to be looked at and fixed: not judged yet, unsure, or serving an outcome since retired. */
  unranked: T[];
}

/**
 * What Today leads with (CER-181): the few next actions that serve the top
 * outcomes, can be acted on now and cost something if they slip, by the
 * stored rank. It only chooses among next actions that are neither in Focus
 * nor Due: a focus pick and a due date always show in their own sections and
 * are never pushed out by a higher-ranked undated task. Everything else
 * keeps its place, ordered by the same rank within its context. Null when
 * there are no outcomes, so Today is as it was.
 */
export function todayLead<T extends GtdTodayTask>(
  view: GtdToday<T>,
  ranks: ReadonlyMap<string, ItemRank>,
  ranked: readonly Outcome[],
  limit: number = TODAY_LEAD_COUNT,
): TodayLead<T> | null {
  if (ranked.length === 0) return null;
  const rankOf = (task: T) => ranks.get(`task:${task.id}`);
  const byRank = (tasks: readonly T[]) =>
    tasks.map((task, index) => ({ task, index })).sort((a, b) => compareRanks(rankOf(a.task), rankOf(b.task)) || a.index - b.index).map((e) => e.task);
  const next = view.next.map((group) => ({ ...group, tasks: byRank(group.tasks) }));
  const all = next.flatMap((group) => group.tasks);
  const lead = byRank(all).flatMap((task) => {
    const rank = rankOf(task);
    const outcome = rank?.ranked && rank.score > 0 ? ranked.find((o) => o.id === rank.outcomeId) : undefined;
    return outcome ? [{ task, outcome }] : [];
  }).slice(0, limit);
  return { lead, view: { ...view, next }, unranked: all.filter((task) => !rankOf(task)?.ranked) };
}
